import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Divider, Dropdown } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { Select } from './select.component';
import { SearchBox } from './search-box.component';
import styles from './forms.module.scss';

function Footer({ footer }) {
  if (!footer) return null;
  return (
    <>
      <Divider className={styles.multiSelectFooterDivider} />
      <div className={styles.multiSelectFooter}>{footer}</div>
    </>
  );
}

function ButtonPanel({ name, options = [], value = [], onChange, placeholder, footer }) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(option => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  const toggle = optionValue => {
    const next = value.includes(optionValue)
      ? value.filter(id => id !== optionValue)
      : [...value, optionValue];
    onChange?.(name, next);
  };

  return (
    <div className={styles.multiSelectPanel}>
      <SearchBox
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder={placeholder}
      />
      <ul className={styles.multiSelectList}>
        {filtered.map(option => {
          const selected = value.includes(option.value);
          return (
            <li key={option.value}>
              <button
                type="button"
                className={classNames(styles.multiSelectOption, {
                  [styles.multiSelectOptionSelected]: selected,
                })}
                aria-pressed={selected}
                onClick={() => toggle(option.value)}>
                <span>{option.label}</span>
                {selected && <FontAwesomeIcon icon={faCheck} />}
              </button>
            </li>
          );
        })}
      </ul>
      <Footer footer={footer} />
    </div>
  );
}

/**
 * Multi-select built on the design-system Select with an optional sticky footer
 * rendered below the options (e.g. an action button).
 *
 * Pass `renderTrigger` to open a self-contained panel (search on top, a
 * scrollable checklist, then the footer) from a custom element such as a button.
 * Omitting it preserves the default input-with-chips control.
 *
 * @param {import('./select.component').SelectProps & {
 *   footer?: React.ReactNode,
 *   renderTrigger?: () => React.ReactNode,
 * }} props
 */
export function MultiSelectWithFooter({ footer, renderTrigger, onOpenChange, ...props }) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = next => {
    setOpen(next);
    onOpenChange?.(next);
  };

  if (!renderTrigger) {
    return (
      <Select
        mode="multiple"
        popupRender={menu => (
          <div>
            {menu}
            <Footer footer={footer} />
          </div>
        )}
        {...props}
      />
    );
  }

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      trigger={['click']}
      placement="bottomRight"
      popupRender={() => <ButtonPanel footer={footer} {...props} />}>
      <div>{renderTrigger()}</div>
    </Dropdown>
  );
}
